import { useQuery } from '@tanstack/react-query';
import * as React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

export interface EntityDrawerConfig<TEntity> {
  basePath: string;
  queryKey: string;
  fetchFn: (id: number) => Promise<TEntity | null>;
  supportsCreate?: boolean;
}

export interface EntityDrawerState<TEntity> {
  entity: TEntity | null;
  isOpen: boolean;
  mode: 'view' | 'create' | 'edit';
}

export interface EntityDrawerActions {
  open: (id: number) => void;
  edit: (id: number) => void;
  create: () => void;
  close: () => void;
}

export interface UseEntityDrawerReturn<TEntity> {
  state: EntityDrawerState<TEntity>;
  actions: EntityDrawerActions;
  isLoading: boolean;
}

export function useEntityDrawer<TEntity>(
  config: EntityDrawerConfig<TEntity>,
): UseEntityDrawerReturn<TEntity> {
  const { basePath, queryKey, fetchFn, supportsCreate = true } = config;

  const navigate = useNavigate();
  const { id: urlId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const urlMode = searchParams.get('mode') as 'view' | 'create' | 'edit' | null;

  const [entity, setEntity] = React.useState<TEntity | null>(null);
  const [isOpen, setIsOpen] = React.useState(!!urlId || urlMode === 'create');
  const [mode, setMode] = React.useState<'view' | 'create' | 'edit'>(
    urlMode || 'view',
  );

  const { data: fetchedEntity, isLoading } = useQuery({
    queryKey: [queryKey, urlId],
    queryFn: () => fetchFn(parseInt(urlId!)),
    enabled: !!urlId,
  });

  // Sync state with URL
  React.useEffect(() => {
    if (urlId && fetchedEntity) {
      setEntity(fetchedEntity);
      setIsOpen(true);
      setMode(urlMode === 'edit' ? 'edit' : 'view');
    } else if (urlMode === 'create' && supportsCreate) {
      setEntity(null);
      setIsOpen(true);
      setMode('create');
    } else if (!urlId && urlMode !== 'create') {
      setEntity(null);
      setIsOpen(false);
      setMode('view');
    }
  }, [urlId, fetchedEntity, urlMode, supportsCreate]);

  const open = React.useCallback(
    (id: number) => navigate(`${basePath}/${id}`),
    [navigate, basePath],
  );

  const edit = React.useCallback(
    (id: number) => navigate(`${basePath}/${id}?mode=edit`),
    [navigate, basePath],
  );

  const create = React.useCallback(
    () => navigate(`${basePath}?mode=create`),
    [navigate, basePath],
  );

  const close = React.useCallback(
    () => navigate(basePath),
    [navigate, basePath],
  );

  return {
    state: { entity, isOpen, mode },
    actions: { open, edit, create, close },
    isLoading,
  };
}
